<template>
  <section
    id="socios"
    aria-label="Marcas que confían en APS"
    class="bg-white border-t border-gray-100 overflow-hidden py-14 md:py-20 scroll-mt-20"
  >
    <div class="container-app text-center mb-8 md:mb-10">
      <h2 class="font-display text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight text-navy-900">
        Conoce a nuestros
        <span class="bg-gradient-to-r from-primary-500 to-primary-700 bg-clip-text text-transparent">socios</span>
      </h2>
      <p class="mt-4 inline-flex items-center gap-2 text-sm md:text-base text-navy-500">
        <svg class="w-5 h-5 text-primary-500" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 11l3 3" />
        </svg>
        Da clic en cada logo y conócelos
      </p>
    </div>

    <div class="marquee">
      <div class="marquee-track">
        <button
          v-for="(partner, i) in loopLogos"
          :key="i"
          :aria-label="`Ver información de ${partner.name}`"
          @click="openModal(partner)"
          class="mx-8 flex items-center bg-transparent border-0 cursor-pointer focus:outline-none"
        >
          <img
            :src="`/${partner.file}`"
            :alt="partner.name"
            class="h-12 md:h-14 object-contain grayscale opacity-70 hover:grayscale-0 hover:opacity-100 transition duration-300 pointer-events-none"
          />
        </button>
      </div>
    </div>

    <!-- Modal -->
    <transition name="fade">
      <div
        v-if="modalPartner"
        class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy-900/70 backdrop-blur-sm"
        @click.self="closeModal"
        role="dialog"
        aria-modal="true"
      >
        <div class="relative bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 md:p-10 modal-pop">
          <button
            @click="closeModal"
            class="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center text-navy-500 hover:bg-navy-50 hover:text-navy-900 transition-colors"
            aria-label="Cerrar"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <div class="flex items-center justify-center h-24 mb-6">
            <img :src="`/${modalPartner.file}`" :alt="modalPartner.name" class="max-h-20 max-w-[80%] object-contain" />
          </div>

          <h3 class="font-display text-2xl md:text-3xl font-extrabold text-navy-900 text-center mb-2">
            {{ modalPartner.name }}
          </h3>

          <div v-if="modalPartner.service" class="flex justify-center mb-5">
            <span class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-50 text-primary-700 text-xs font-bold uppercase tracking-widest">
              <span class="w-1.5 h-1.5 bg-primary-500 rounded-full"></span>
              {{ modalPartner.service }}
            </span>
          </div>

          <p class="text-navy-600 text-base leading-relaxed text-center mb-7">
            {{ description(modalPartner) }}
          </p>

          <a
            v-if="modalPartner.href"
            :href="modalPartner.href"
            target="_blank"
            rel="noopener noreferrer"
            class="flex items-center justify-center gap-2 w-full px-6 py-3.5 bg-primary-500 hover:bg-primary-600 text-white font-bold rounded-full transition-colors shadow-aps"
          >
            Visitar sitio web
            <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </a>
        </div>
      </div>
    </transition>
  </section>
</template>

<script>
// La lista de socios se administra en src/data/partners.js (el panel admin no expone Partners)
import { PARTNERS } from '@/data/partners';

// Descripciones cortas por archivo (placeholder — editables aquí mismo cuando tengas el texto oficial)
const PARTNER_DESCRIPTIONS = {
  'socios/bandera.png':    'Empresa mexicana especializada en banderas oficiales y promocionales para escuelas, oficinas y eventos protocolarios. Producción local con material de alta durabilidad.',
  'socios/desing.png':     'Estudio creativo de diseño gráfico, branding e identidad visual. Acompaña a emprendedores y pymes a construir marcas memorables que conectan con su audiencia.',
  'socios/logojoom.png':   'Firma especializada en seguros y créditos para proteger el patrimonio y hacer crecer el capital de familias y empresas mexicanas con asesoría personalizada.',
  'socios/logojobman.png': 'Inmobiliaria con presencia en CDMX y Edomex. Especialistas en propiedades residenciales y comerciales, con acompañamiento legal y financiero en todo el proceso.',
  'socios/recc.png':       'Real Estate Consulting Corp. Asesoría legal especializada en arrendamiento inmobiliario: pólizas, contratos, gestión inmobiliaria y servicios contables.',
  'socios/adnexis.svg':    'Data Hub que combina estrategia, tecnología y crecimiento. Conecta plataformas, procesa señales y entrega resultados medibles desde el primer mes.',
};

export default {
  name: 'ThePartners',
  data() {
    return {
      logos: PARTNERS,
      modalPartner: null,
    };
  },
  computed: {
    loopLogos() {
      return Array(6).fill(this.logos).flat();
    }
  },
  beforeDestroy() {
    this.removeEscListener();
    this.restoreBodyScroll();
  },
  methods: {
    description(p) {
      if (!p) return '';
      return PARTNER_DESCRIPTIONS[p.file] || `${p.name} es un socio estratégico de APS Pensiones.`;
    },
    openModal(partner) {
      this.modalPartner = partner;
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', this.onEsc);
    },
    closeModal() {
      this.modalPartner = null;
      this.restoreBodyScroll();
      this.removeEscListener();
    },
    onEsc(e) {
      if (e.key === 'Escape') this.closeModal();
    },
    removeEscListener() {
      document.removeEventListener('keydown', this.onEsc);
    },
    restoreBodyScroll() {
      document.body.style.overflow = '';
    },
  }
};
</script>

<style scoped>
.marquee {
  width: 100%;
  -webkit-mask-image: linear-gradient(to right, transparent, black 8%, black 92%, transparent);
          mask-image: linear-gradient(to right, transparent, black 8%, black 92%, transparent);
}
.marquee-track {
  display: flex;
  width: max-content;
  animation: marquee 35s linear infinite;
}
@media (hover: hover) and (pointer: fine) {
  .marquee:hover .marquee-track { animation-play-state: paused; }
}
@media (prefers-reduced-motion: reduce) {
  .marquee-track { animation: none; }
}

/* Modal */
.fade-enter-active, .fade-leave-active { transition: opacity .2s ease; }
.fade-enter, .fade-leave-to { opacity: 0; }
.modal-pop {
  animation: modal-pop .25s ease-out;
}
@keyframes modal-pop {
  from { transform: scale(.94); opacity: 0; }
  to   { transform: scale(1);    opacity: 1; }
}
</style>
